import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import { BrandModel, CurrencyModel, ImageModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import useStructuredData from "@store/Hooks/StructuredData/useStructuredData";
import { useCurrency } from "@store/Hooks/useCurrency/useCurrency";
import { Brand, Offer, Product } from "@store/Schemas/structuredData";
import { FC, useContext, useEffect, useState } from "react";

interface OwnProps {
    useZeroPrice: boolean;
}

const ProductStructuredData: FC<OwnProps> = ({ useZeroPrice }) => {
    const { product, productInfo } = useContext(ProductContext);
    const [data, setData] = useState<Product>();
    const currency = useCurrency();

    useEffect(() => {
        if (!product || !window?.location) {
            return;
        }

        setData({
            "@type": "Product",
            name: product.productTitle,
            description: product.content ? getDescription(product.content.htmlContent) : "",
            image: product.images ? getImages(product.images, window.location.origin) : "",
            offers: getOffers(product, productInfo, currency, window.location.origin, useZeroPrice),
            sku: product.productNumber,
            brand: product.brand ? getBrand(product.brand) : "",
            mpn: product.detail?.modelNumber,
        });
    }, [product, productInfo]);

    useStructuredData(data);

    return null;
};

export const getDescription = (html: string) => {
    if (!html) {
        return "";
    }

    return html.replace(/(<([^>]+)>)/gi, "").replace(/(\r\n\t|\n|\r|\t)/gm, "");
};

export const getBrand = (brand: BrandModel): Brand | string => {
    if (!brand) {
        return "";
    }

    return {
        "@type": "Brand",
        name: brand.name,
    };
};

export const getImages = (images: ImageModel[], baseUrl: string) => {
    const imageData: string[] = [];

    if (!images || images.length === 0 || !baseUrl) {
        return imageData;
    }

    images.forEach(image => imageData.push(baseUrl + image.mediumImagePath));

    return imageData;
};

export const getOffers = (
    product: ProductModel,
    productInfo: ProductInfo,
    currency: CurrencyModel | null,
    baseUrl: string,
    useZeroPrice: boolean,
) => {
    const offers: Offer[] = [];

    if (!product || !productInfo || !currency) {
        return offers;
    }

    offers.push({
        "@type": "Offer",
        url: baseUrl + productInfo.productDetailPath,
        priceCurrency: currency.currencyCode,
        price: getPrice(productInfo, useZeroPrice),
        sku: product.productNumber,
        name: product.productTitle,
    });

    return offers;
};

export const getPrice = (productInfo: ProductInfo, useZeroPrice: boolean) => {
    if (!productInfo.pricing) {
        return "";
    }

    if (!useZeroPrice) {
        return productInfo.pricing?.actualPrice > 0 ? productInfo.pricing?.actualPrice : "";
    }

    return productInfo.pricing?.actualPrice;
};

export default ProductStructuredData;
